"use client";

import Link from "next/link";
import Image from "next/image";
import { X, CalendarDays, Phone } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { resolveLegacyLink } from "../utils/linkUtils";
import { buildProspectLink } from "../utils/prospectLink";
import AddressInput from "../design-system/molecules/AddressInput";
import { Agency } from "../context/GeoContext";

type PreCommandePopupProps = {
    onClose: () => void;
    parcoursHref?: string;
};

export default function PreCommandePopup({ onClose, parcoursHref }: PreCommandePopupProps) {
    const [mounted, setMounted] = useState(false);
    const [commanderHref, setCommanderHref] = useState<string>(
        resolveLegacyLink("/parcours-prospects", "clients")
    );
    const [selectedAgency,] = useState<Agency | null>(null);
    const agencyPhone = selectedAgency?.phone || "";
    const [showUrgentPopup, setShowUrgentPopup] = useState(false);

    // Portal : on ne peut appeler document.body qu'après le montage côté client
    useEffect(() => {
        setMounted(true);
    }, []);

    // Bonus : empêcher le scroll du body pendant que la popup est ouverte
    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "";
        };
    }, []);

    useEffect(() => {
        if (parcoursHref) {
            setCommanderHref(parcoursHref);
            return;
        }
        const link =
            buildProspectLink() || resolveLegacyLink("/parcours-prospects", "clients");
        setCommanderHref(link);
    }, [parcoursHref]);

    if (!mounted) return null;

    const popupContent = (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
            <div className="relative w-full max-w-[500px] rounded-[18px] min-h-[450px] bg-white px-6 py-10 shadow-[0_20px_60px_rgba(0,0,0,0.25)] md:max-w-[600px]">
                {/* Bouton fermer */}
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Fermer la pop-up"
                    className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-white transition hover:bg-primaryGreen"
                >
                    <X size={18} />
                </button>

                {/* Icône */}
                <div className="mx-auto mb-4 flex justify-center">
                    <Image
                        src="https://site-lms.fra1.digitaloceanspaces.com/Frame_546_7695d349e8.svg"
                        alt="pré-commande Les Menus Services"
                        width={96}
                        height={96}
                        className="h-24 w-24"
                    />
                </div>

                {!showUrgentPopup ? (
                    <>
                        {/* Texte principal */}
                        <div className="text-center">
                            <h2 className="font-playfair text-[24px] lg:text-[30px] font-bold leading-snug text-text">
                                Commande possible à partir de
                                <br />
                                <span className="text-primaryGreen">
                                    2 jours ouvrés à l’avance
                                </span>
                            </h2>

                            <div className="mx-auto mt-4 h-[2px] w-16 rounded-full bg-primaryGreen" />

                            <p className="mx-auto mt-5 font-roboto text-[16px] lg:text-lg font-medium leading-relaxed text-text">
                                Confirmation nécessaire de votre agence qui vous contactera dès
                                réception de votre commande.
                            </p>
                        </div>

                        {/* Séparateur */}
                        <div className="mx-auto my-3 h-[1.5px] w-[50%] rounded-full bg-gray-200" />

                        {/* Question */}
                        <p className="mb-4 text-center font-playfair text-[28px] font-bold text-text">
                            Quelle est votre situation ?
                        </p>

                        {/* CTA */}
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {/* CTA gauche : ouvre la deuxième popup */}
                            <button
                                type="button"
                                onClick={() => setShowUrgentPopup(true)}
                                className=" flex min-h-[72px] items-center justify-start gap-3 rounded border border-gray-300 bg-white px-4 py-3 text-left text-[16px] font-semibold leading-tight text-text transition hover:border-primaryGreen hover:text-primaryGreen "
                            >
                                <CalendarDays size={22} className="shrink-0" />

                                <span className="block flex-1 text-left">
                                    J’ai besoin d’une livraison aujourd’hui ou demain
                                </span>
                            </button>

                            {/* CTA droit : parcours */}
                            <Link
                                href={commanderHref}
                                className=" flex min-h-[72px] items-center justify-start gap-3 rounded bg-secondary px-4 py-3 text-left text-[16px] font-semibold leading-tight text-white shadow-md transition hover:bg-primaryGreen "
                            >
                                <CalendarDays size={22} className="shrink-0" />

                                <span className="block flex-1 text-left">
                                    Je commande pour dans 2 jours ouvrés ou plus
                                </span>
                            </Link>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="text-center">
                            <p className="mx-auto max-w-[480px] font-roboto text-[16px] font-medium leading-relaxed text-text">
                                Renseignez votre adresse afin d’être mis en relation avec l’agence la
                                plus proche, qui pourra étudier la faisabilité de votre demande et vous
                                accompagner.
                            </p>
                        </div>

                        <div className="mt-8">
                            <label className="mb-2 block text-sm font-bold text-text">
                                Votre adresse : 
                            </label>

                            <AddressInput
                                redirectOnSelect={false}
                                showAgencyResult={true}
                                persistAgency={false}
                            />
                        </div>

                        {agencyPhone && (
                            <div className="mt-8 flex justify-center">
                                <a
                                    href={`tel:${agencyPhone.replace(/\s/g, "")}`}
                                    className="flex items-center justify-center gap-3 rounded-md bg-secondary px-8 py-4 text-white transition hover:bg-primaryGreen "
                                >
                                    <Phone size={20} />

                                    <span className="text-center leading-tight">
                                        <strong className="block text-xl font-bold">
                                            {agencyPhone}
                                        </strong>
                                        <span className="block text-sm font-semibold">
                                            Appeler l’agence
                                        </span>
                                    </span>
                                </a>
                            </div>
                        )}


                    </>
                )}
            </div>
        </div>
    );

    return createPortal(popupContent, document.body);
}